import { $apiV2, API_URL_V2 } from "../config/api";
import { APP_VERSION } from "../config/version";
import {
  TRACKING_SCHEMA_VERSION,
  buildSanitizedRoute,
  compactTrackingObject,
  ensureDeviceId,
  getDeviceContext,
  generateTrackingId,
  getSourceSectionFromPath,
  isTrackableBusinessPath,
} from "./utils";

const STORAGE_KEYS = {
  authMarker: "tracking_session_auth_marker",
  sessionId: "tracking_session_id",
};

const TRACKING_BATCH_ENDPOINT = "/tracking/events/batch";
const TRACKING_BEACON_ENDPOINT = `${API_URL_V2}/tracking/events/beacon`;
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;
const RETRY_DELAYS_MS = [1000, 5000, 15000];

class TrackingService {
  constructor() {
    this.queue = [];
    this.isFlushing = false;
    this.flushTimeoutId = null;
    this.retryAttempt = 0;
    this.currentRoute = null;
    this.currentPageViewId = null;
    this.hasBoundLifecycleHandlers = false;

    this.handlePageLifecycleFlush = this.handlePageLifecycleFlush.bind(this);
    this.bindLifecycleHandlers();
  }

  bindLifecycleHandlers() {
    if (this.hasBoundLifecycleHandlers || typeof window === "undefined") {
      return;
    }

    window.addEventListener("pagehide", this.handlePageLifecycleFlush);
    window.addEventListener("beforeunload", this.handlePageLifecycleFlush);
    this.hasBoundLifecycleHandlers = true;
  }

  handlePageLifecycleFlush() {
    this.flush({ useBeacon: true });
  }

  isTrackingEnabled() {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return Boolean(localStorage.getItem("token"));
    } catch (error) {
      return false;
    }
  }

  getSessionId() {
    if (typeof window === "undefined") {
      return null;
    }

    const tokenMarker = localStorage.getItem("token") || "";
    const currentSessionId = sessionStorage.getItem(STORAGE_KEYS.sessionId);
    const currentAuthMarker = sessionStorage.getItem(STORAGE_KEYS.authMarker);

    if (!currentSessionId || currentAuthMarker !== tokenMarker) {
      const nextSessionId = generateTrackingId();
      sessionStorage.setItem(STORAGE_KEYS.sessionId, nextSessionId);
      sessionStorage.setItem(STORAGE_KEYS.authMarker, tokenMarker);
      return nextSessionId;
    }

    return currentSessionId;
  }

  getTrackingContext(routeOverride = null) {
    const fallbackPathname = typeof window !== "undefined" ? window.location.pathname : "";
    const fallbackSearch = typeof window !== "undefined" ? window.location.search : "";
    const route = routeOverride || this.currentRoute || buildSanitizedRoute(
      fallbackPathname,
      fallbackSearch
    );

    return {
      device_id: ensureDeviceId(),
      page_view_id: this.currentPageViewId,
      route,
      schema_version: TRACKING_SCHEMA_VERSION,
      session_id: this.getSessionId(),
      source_section: getSourceSectionFromPath(route.split("?")[0]),
    };
  }

  createEvent(payload) {
    if (!this.isTrackingEnabled()) {
      return null;
    }

    const context = this.getTrackingContext(payload?.route);
    const route = payload?.route || context.route;
    const sourceSection =
      payload?.sourceSection ||
      payload?.source_section ||
      context.source_section ||
      getSourceSectionFromPath(route.split("?")[0]);

    return {
      action: payload.action || payload.eventType,
      device_id: context.device_id,
      entity_id: payload.entityId ?? null,
      entity_label: payload.entityLabel ?? null,
      entity_type: payload.entityType || null,
      event_type: payload.eventType,
      event_uuid: generateTrackingId(),
      metadata: compactTrackingObject({
        ...(payload.metadata || {}),
        app_version: APP_VERSION,
        device: getDeviceContext(),
      }),
      occurred_at: new Date().toISOString(),
      page_view_id: payload.pageViewId || context.page_view_id,
      route,
      schema_version: context.schema_version,
      session_id: context.session_id,
      source_section: sourceSection,
    };
  }

  scheduleFlush(delay = FLUSH_INTERVAL_MS) {
    if (typeof window === "undefined") {
      return;
    }

    if (this.flushTimeoutId) {
      window.clearTimeout(this.flushTimeoutId);
    }

    this.flushTimeoutId = window.setTimeout(() => {
      this.flush();
    }, delay);
  }

  enqueueEvent(payload) {
    const event = this.createEvent(payload);

    if (!event) {
      return null;
    }

    this.queue.push(event);

    if (this.queue.length >= MAX_BATCH_SIZE) {
      this.flush();
    } else {
      this.scheduleFlush();
    }

    return event;
  }

  track(payload) {
    try {
      return this.enqueueEvent(payload);
    } catch (error) {
      return null;
    }
  }

  trackPageView({ pathname = "", search = "" }) {
    if (!isTrackableBusinessPath(pathname)) {
      this.currentRoute = null;
      this.currentPageViewId = null;
      return null;
    }

    const nextRoute = buildSanitizedRoute(pathname, search);

    if (nextRoute === this.currentRoute) {
      return null;
    }

    const previousRoute = this.currentRoute;
    const nextPageViewId = generateTrackingId();

    this.currentRoute = nextRoute;
    this.currentPageViewId = nextPageViewId;

    return this.track({
      action: "view",
      eventType: "page_view",
      metadata: {
        from_route: previousRoute,
        to_route: nextRoute,
      },
      pageViewId: nextPageViewId,
      route: nextRoute,
      sourceSection: getSourceSectionFromPath(pathname),
    });
  }

  async postBatch(batch) {
    await $apiV2.post(TRACKING_BATCH_ENDPOINT, {
      events: batch,
      sent_at: new Date().toISOString(),
    });
  }

  flushWithBeacon(batch) {
    if (typeof navigator === "undefined") {
      return false;
    }

    const payload = JSON.stringify({
      events: batch,
      sent_at: new Date().toISOString(),
    });

    if (typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([payload], { type: "application/json" });
        return navigator.sendBeacon(TRACKING_BEACON_ENDPOINT, blob);
      } catch (error) {
        return false;
      }
    }

    if (typeof fetch === "function") {
      fetch(TRACKING_BEACON_ENDPOINT, {
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
        keepalive: true,
        method: "POST",
      }).catch(() => {});
      return true;
    }

    return false;
  }

  async flush({ useBeacon = false } = {}) {
    if (!this.queue.length) {
      return;
    }

    if (this.isFlushing && !useBeacon) {
      return;
    }

    const batch = useBeacon
      ? [...this.queue]
      : this.queue.splice(0, MAX_BATCH_SIZE);

    if (!useBeacon) {
      this.isFlushing = true;
    }

    if (this.flushTimeoutId && typeof window !== "undefined") {
      window.clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = null;
    }

    try {
      if (useBeacon) {
        const beaconSent = this.flushWithBeacon(batch);

        if (beaconSent) {
          this.queue = this.queue.filter(
            (queuedEvent) => !batch.some((batchEvent) => batchEvent.event_uuid === queuedEvent.event_uuid)
          );
        }
        return;
      }

      await this.postBatch(batch);
      this.retryAttempt = 0;

      if (this.queue.length) {
        this.scheduleFlush(0);
      }
    } catch (error) {
      this.queue = [...batch, ...this.queue];
      const retryDelay = RETRY_DELAYS_MS[Math.min(this.retryAttempt, RETRY_DELAYS_MS.length - 1)];
      this.retryAttempt += 1;
      this.scheduleFlush(retryDelay);
    } finally {
      if (!useBeacon) {
        this.isFlushing = false;
      }
    }
  }

  resetForTests() {
    if (this.flushTimeoutId && typeof window !== "undefined") {
      window.clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = null;
    }

    this.queue = [];
    this.isFlushing = false;
    this.retryAttempt = 0;
    this.currentRoute = null;
    this.currentPageViewId = null;
  }

  getQueueSnapshotForTests() {
    return [...this.queue];
  }
}

export const trackingService = new TrackingService();
